// Tournament Management Module - TypeScript Models
// Mirrors backend view models and enums for the Tournament Management feature.

// ============================================================================
// Enums (numeric, matching backend C# enum values)
// ============================================================================

export enum TournamentStatus {
  Upcoming = 0,
  Active = 1,
  Completed = 2,
  Cancelled = 3,
}

export enum TournamentRecurrency {
  OneTime = 0,
  Weekly = 1,
  BiWeekly = 2,
  Monthly = 3,
  Quarterly = 4,
  Yearly = 5,
}

export enum TournamentFormat {
  League = 0,
  Knockout = 1,
  GroupStage = 2,
}

export enum MatchStatus {
  Scheduled = 0,
  InProgress = 1,
  Completed = 2,
  Closed = 3,
  UnderRevision = 4,
  Cancelled = 5,
}

/**
 * Client mirror of the server's explicit match status transition table
 * (`TournamentHelpers.AllowedStatusTransitions` — keep both in sync). The status dropdown offers
 * ONLY these targets; the server returns 400 for anything else.
 *   Scheduled → InProgress, Cancelled
 *   InProgress → Completed (through the point calculation), Cancelled
 *   Completed → Closed, UnderRevision, Cancelled
 *   UnderRevision → Completed, Cancelled
 *   Closed → UnderRevision (HR "reopen")
 *   Cancelled → Scheduled (HR "restore")
 */
export const allowedMatchStatusTargets: Record<MatchStatus, MatchStatus[]> = {
  [MatchStatus.Scheduled]: [MatchStatus.InProgress, MatchStatus.Cancelled],
  [MatchStatus.InProgress]: [MatchStatus.Completed, MatchStatus.Cancelled],
  [MatchStatus.Completed]: [MatchStatus.Closed, MatchStatus.UnderRevision, MatchStatus.Cancelled],
  [MatchStatus.UnderRevision]: [MatchStatus.Completed, MatchStatus.Cancelled],
  [MatchStatus.Closed]: [MatchStatus.UnderRevision],
  [MatchStatus.Cancelled]: [MatchStatus.Scheduled],
};

export enum MatchType {
  Outdoor = 0,
  Indoor = 1,
}

export enum MatchFormat {
  FiveAside = 0,
  SevenAside = 1,
  NineAside = 2,
  ElevenAside = 3,
}

export enum MatchRole {
  HeadReferee = 0,
  AssistantReferee = 1,
  FourthOfficial = 2,
  Commissioner = 3,
}

export enum RefereeCertification {
  NationalLevelA = 0,
  AFCElite = 1,
  FIFAInternational = 2,
}

export enum RefereeAvailability {
  Available = 0,
  Busy = 1,
  Unavailable = 2,
}

export enum AssignmentResponse {
  Pending = 0,
  Accepted = 1,
  Declined = 2,
  /**
   * CO-762: the response deadline passed with no answer. NOT a decline — the assignment stays on the
   * roster, keeps its shift and its pay, and can still be accepted. Mirrors the server's
   * `AssignmentResponse.Lapsed`; if the server has not been widened yet this value simply never
   * arrives. Render it through `AssignmentResponseChip`, never as "Declined".
   */
  Lapsed = 3,
}

export enum AssignmentAction {
  Assigned = 0,
  Reassigned = 1,
  Removed = 2,
  AcceptedByReferee = 3,
  DeclinedByReferee = 4,
  NotificationFailed = 5,
  /** Internal delivery progress; the server never returns it from GetAssignmentHistory. */
  HrDigestPending = 6,
  /** CO-762: HR confirmed (or, CO-921, recorded on a played match) the assignment for the official. */
  ConfirmedByHr = 7,
  /** CO-762: the response deadline passed unanswered. */
  Lapsed = 8,
  /** CO-843: internal; excluded from the history the server returns. */
  QuietHoursHeld = 9,
  /**
   * CO-921: a reconcile changed this member's work points. `reason` reads
   * "<old> -> <new> pts: <trigger>" (points only, never money).
   */
  WorkPointsReconciled = 10,
}

export enum AgeCategory {
  U06 = 0, U07 = 1, U08 = 2, U09 = 3, U10 = 4,
  U11 = 5, U12 = 6, U13 = 7, U14 = 8, U15 = 9,
  U16 = 10, U17 = 11, U18 = 12, U19 = 13,
  U21 = 14, Senior = 15,
}

// ============================================================================
// Tournament Views
// ============================================================================

export interface TournamentView {
  id: number;
  name: string;
  description?: string;
  venue: string;
  startDate: string;
  endDate: string;
  participants: number;
  status: TournamentStatus;
  format: TournamentFormat;
  recurrency: TournamentRecurrency;
  matchCount: number;
  createdOn: string;
  /** CO-922: true on rows of the HR-only Archived list (`GetTournaments?archived=true`). */
  isArchived?: boolean;
}

export interface TournamentListView {
  items: TournamentView[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface TournamentStatsView {
  totalTournaments: number;
  activeTournaments: number;
  upcomingTournaments: number;
  completedTournaments: number;
  totalMatches: number;
}

// ============================================================================
// Match Views
// ============================================================================

export interface MatchView {
  id: number;
  tournamentId: number;
  tournamentName: string;
  team1Name: string;
  team2Name: string;
  location: string;
  pitch?: string;
  ageCategory?: number | string;  // API sends string, normalize with resolveEnum
  division?: string;
  scheduledDateTime: string;
  /** True when only the date is known and the kickoff time is unset — render "Time TBD". */
  scheduledTimeUnknown: boolean;
  /**
   * Kickoff resolved against the COMPANY timezone by the server. Optional: when absent the client
   * falls back to a viewer-clock comparison (`utils/matchTiming.ts::hasMatchKickedOff`).
   */
  hasStarted?: boolean;
  status: MatchStatus;
  type: MatchType;
  format: MatchFormat;
  /** Duration snapshot in minutes — the length of the referees' shifts for this match. */
  durationMinutes?: number;
  /** True when the duration was set on this match instead of coming from the format's company default. */
  durationOverridden?: boolean;
  team1Score?: number;
  team2Score?: number;
  extraTimeTeam1Score?: number;
  extraTimeTeam2Score?: number;
  penaltyTeam1Score?: number;
  penaltyTeam2Score?: number;
  winnerTeamName?: string;
  completionComments?: string;
  /** Reason given with the last HR status change (cancel / return for revision / reopen / restore). */
  statusReason?: string;
  attachmentUrl?: string;
  attachmentUploadedById?: string;
  attachmentUploadedByName?: string;
  attachmentUploadedOn?: string;
  requiredCertification?: RefereeCertification;
  refereeTeamMembers: RefereeTeamMemberView[];
}

export interface RefereeTeamMemberView {
  id: number;
  userId: string;
  userName: string;
  role: MatchRole;
  workPoints: number;
  workPointOverride?: number;
  workPointBreakdown?: string;
  /** Set once this assignment has been paid through a tournament-compensation payroll entry. */
  payrollVariablePayId?: number;
  phone?: string;
  email?: string;
  matchRating?: number;
  /**
   * CO-764: has this official answered? Arrives as the enum NAME or its number — always normalise
   * with `resolveEnum(AssignmentResponse, …)`, and render through `AssignmentResponseChip` so an
   * unrecognised value cannot come out blank. Optional: an older server build omits it.
   */
  responseStatus?: AssignmentResponse | number | string;
  /** CO-764: the UTC instant the response was due (parse with `parseApiUtc`, never render raw). */
  responseDeadline?: string | null;
}

export interface MatchListView {
  items: MatchView[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  availableLocations: string[];
  availablePitches: string[];
  availableDivisions: string[];
}

export interface ImportResultView {
  importedCount: number;
  skippedCount: number;
  errors: string[];
}

// ============================================================================
// Referee Views
// ============================================================================

export interface RefereeView {
  id: number;
  userId: string;
  name: string;
  photoUrl?: string;
  certification: RefereeCertification;
  phone?: string;
  email?: string;
  location?: string;
  rating: number;
  totalMatches: number;
  /** Per-referee point-to-currency override; null/undefined = company-wide rule rate applies. */
  pointToCurrencyRate?: number | null;
}

export interface RefereeListView {
  items: RefereeView[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  availableCount: number;
}

export interface RefereeRecommendationView {
  refereeProfileId: number;
  name: string;
  photoUrl?: string;
  certification: RefereeCertification;
  fitScore: number;
  fitReasons: string[];
  rank: number;
  estimatedWorkPoints: number;
}

export interface RefereeScheduleView {
  date: string;
  status: RefereeAvailability;
  matchId?: number;
  matchDescription?: string;
  matchTime?: string;
  matchLocation?: string;
  matchStatus?: string;
  reason?: string;
}

/** CO-921: GET Referee/GetClockingPresence (HR only). Booleans only, never the clock-in itself. */
export interface ClockingPresenceView {
  userId: string;
  hasClockIn: boolean;
}

export interface AssignmentHistoryView {
  id: number;
  refereeName: string;
  action: AssignmentAction;
  previousRefereeName?: string;
  reason?: string;
  notificationSent: boolean;
  createdOn: string;
}

export interface AssignmentResultView {
  matchId: number;
  refereeProfileId: number;
  action: AssignmentAction;
  notificationSent: boolean;
  message: string;
}

export interface AssignmentResponseResultView {
  matchId: number;
  newStatus: AssignmentResponse;
  message: string;
}

export interface RespondToAssignmentInput {
  matchId: number;
  reason?: string;
}

// ============================================================================
// Work Point Views
// ============================================================================

export interface WorkPointRoleFormatPoints {
  role: MatchRole;
  format: MatchFormat;
  basePoints: number;
  indoorBonus: number;
  outdoorBonus: number;
}

export interface WorkPointRuleView {
  roleFormatPoints: WorkPointRoleFormatPoints[];
  id: number;
  weekendHolidayBonus: number;
  nightMatchBonus: number;
  nightMatchThresholdHour: number;
  earlyMorningBonus: number;
  earlyMorningThresholdHour: number;
  highRatingThreshold: number;
  highRatingBonus: number;
  midRatingThreshold: number;
  midRatingBonus: number;
  minPointsPerMatch: number;
  maxPointsPerMatch: number;
  pointToCurrencyRate: number;
  effectiveFrom: string;
  effectiveTo?: string;
  isActive: boolean;
}

/** Company-wide match length per format, in minutes. `isDefault` = the company never saved its own values. */
export interface MatchFormatDurationsView {
  fiveAsideMinutes: number;
  sevenAsideMinutes: number;
  nineAsideMinutes: number;
  elevenAsideMinutes: number;
  isDefault?: boolean;
}

export interface UpdateMatchFormatDurationsInput {
  fiveAsideMinutes: number;
  sevenAsideMinutes: number;
  nineAsideMinutes: number;
  elevenAsideMinutes: number;
}

/** Allowed range for a match duration (settings and per-match override), mirrored from the server. */
export const MATCH_DURATION_MIN_MINUTES = 5;
export const MATCH_DURATION_MAX_MINUTES = 1200;

/** Built-in durations used when a company has not saved its own (server `MatchFormatDuration` defaults). */
export const DEFAULT_MATCH_FORMAT_DURATIONS: MatchFormatDurationsView = {
  fiveAsideMinutes: 25,
  sevenAsideMinutes: 40,
  nineAsideMinutes: 50,
  elevenAsideMinutes: 59,
  isDefault: true,
};

/** The company duration for one format. */
export function durationForFormat(durations: MatchFormatDurationsView, format: MatchFormat): number {
  switch (format) {
    case MatchFormat.FiveAside: return durations.fiveAsideMinutes;
    case MatchFormat.SevenAside: return durations.sevenAsideMinutes;
    case MatchFormat.NineAside: return durations.nineAsideMinutes;
    default: return durations.elevenAsideMinutes;
  }
}

export interface WorkPointEstimateView {
  estimatedPoints: number;
  estimatedCurrencyAmount: number;
  breakdown: string;
  baseRolePoints: number;
  formatPoints: number;
  typeBonus: number;
  timeBonus: number;
  performanceBonus: number;
  rawSubtotal: number;
  minPointsPerMatch: number;
  maxPointsPerMatch: number;
  pointToCurrencyRate: number;
  isNonWorkingDay: boolean;
}

export interface WorkPointBreakdownView {
  refereeTeamMemberId: number;
  refereeName: string;
  role: MatchRole;
  totalPoints: number;
  baseRolePoints: number;
  formatPoints: number;
  typeBonus: number;
  timeBonus: number;
  performanceBonus: number;
  calculatedPoints: number;
  isOverridden: boolean;
  currencyAmount: number;
  /** Rate applied to currencyAmount (referee override or rule rate); 0 on breakdowns stored before this field existed. */
  pointToCurrencyRate?: number;
}

export interface CompensationReportView {
  lines: RefereeCompensationLine[];
  /** The active rule's company-wide rate (shown once in the report header); null when no active rule. */
  ruleRate?: number | null;
  /** Referees on the report (all pages) whose line used a referee-level override. */
  customRateCount?: number;
  totalWorkPoints: number;
  totalCurrencyAmount: number;
  startDate: string;
  endDate: string;
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface RefereeCompensationLine {
  refereeUserId: string;
  refereeName: string;
  matchCount: number;
  totalWorkPoints: number;
  totalCurrencyAmount: number;
  /** Rate applied to this line (referee override when set, else the active rule rate). */
  pointToCurrencyRate?: number;
  /** True when the line was priced with the referee's own override rather than the rule rate. */
  isCustomRate?: boolean;
  /** The active rule's rate at the time of the report. */
  ruleRate?: number;
}

export interface PayrollEntriesResultView {
  entriesCreated: number;
  refereesProcessed: number;
  totalAmount: number;
  startDate: string;
  endDate: string;
  message?: string;
  entries: PayrollEntryLine[];
}

export interface PayrollEntryLine {
  refereeUserId: string;
  refereeName: string;
  totalWorkPoints: number;
  currencyAmount: number;
  /** Rate applied to this entry (referee override when set, else the active rule rate). */
  pointToCurrencyRate?: number;
  matchCount: number;
  variablePayId: number;
}

// ============================================================================
// My Assignments / Self-Service Views
// ============================================================================

export interface MyAssignmentsView {
  items: MyAssignmentItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface MyAssignmentItem {
  matchId: number;
  tournamentId: number;
  tournamentName: string;
  team1Name: string;
  team2Name: string;
  location: string;
  scheduledDateTime: string;
  matchStatus: MatchStatus;
  role: MatchRole;
  workPoints: number;
  responseStatus: number;
  responseDeadline: string | null;
}

export interface MyWorkPointsSummaryView {
  totalWorkPoints: number;
  totalCurrencyAmount: number;
  matchCount: number;
  recentMatches: MyAssignmentItem[];
}

// ============================================================================
// Input Interfaces (for API calls)
// ============================================================================

export interface CreateTournamentInput {
  name: string;
  description?: string;
  venue: string;
  startDate: string;
  endDate: string;
  participants: number;
  recurrency?: TournamentRecurrency;
  format?: TournamentFormat;
}

export interface UpdateTournamentInput {
  id: number;
  name: string;
  description?: string;
  venue: string;
  startDate: string;
  endDate: string;
  participants: number;
  status: TournamentStatus;
  recurrency: TournamentRecurrency;
  format?: TournamentFormat;
}

export interface TournamentListQuery {
  search?: string;
  status?: TournamentStatus;
  startDateFrom?: string;
  startDateTo?: string;
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
  /** CO-922: list ARCHIVED tournaments instead of live ones. HR only; anyone else gets 403. */
  archived?: boolean;
}

// ── CO-922: HR cascade delete (owner decisions 2026-10-21) ────────────────────────────────────

/** What HR answers about a recurring series. Mirrors server `TournamentSeriesChoice`. */
export enum TournamentSeriesChoice {
  Continue = 0,
  EndSeries = 1,
}

export interface TournamentDeletionCurrencyAmount {
  currencyId: number;
  currencyCode?: string | null;
  amount: number;
  entryCount: number;
}

export interface TournamentDeletionRatingChange {
  userId: string;
  fullName?: string | null;
  before: number;
  after: number;
  resetToZero: boolean;
}

export interface TournamentDeletionRecurringInfo {
  isRecurring: boolean;
  recurrency: TournamentRecurrency | number | string;
  parentTournamentId?: number | null;
  liveChildCount: number;
  nextOccurrenceWouldBeRecreated: boolean;
  otherSeriesTournamentCount: number;
}

/* ─── CO-921/CO-922 Round 2: HR consent for pay claw-backs ─────────────────────────────────────
 * Mirrors Mowazaf.Server ClawbackPreviewView. A change that would take back pay already given to
 * officials is refused with 409 (codes below) carrying this preview in errors[0].details; HR approves
 * by resending the same request with header X-Clawback-Consent = consentToken. */
export const CLAWBACK_CONSENT_HEADER = 'X-Clawback-Consent';
export const CLAWBACK_CONSENT_REQUIRED = 'clawbackConsentRequired';
export const CLAWBACK_CONSENT_STALE = 'clawbackConsentStale';
export const CLAWBACK_REQUIRES_HR = 'clawbackRequiresHr';

/** `pendingEntry`: not on a payslip yet (reduced/removed from the pending entry). `nextPayroll`: already paid, deducted next payroll. */
export type ClawbackKind = 'pendingEntry' | 'nextPayroll';

export type ClawbackOperation =
  | 'replaceOfficial'
  | 'editMatch'
  | 'deleteMatch'
  | 'updateResults'
  | 'cancelMatch'
  | 'recalculate'
  | 'bulkImport'
  | 'deleteTournament';

export interface ClawbackAmountLine {
  currencyId: number;
  currencyCode: string;
  kind: ClawbackKind | string;
  amount: number;
}

export interface ClawbackOfficial {
  userId: string;
  fullName: string;
  lines: ClawbackAmountLine[];
  /** The official's points survive the change, so the next pay generation pays them again at `pointsAfter`. */
  repaidOnNextGeneration: boolean;
  pointsAfter: number;
}

export interface ClawbackCurrencyTotal {
  currencyId: number;
  currencyCode: string;
  pendingEntry: number;
  nextPayroll: number;
}

export interface ClawbackRow {
  rowIndex: number;
  matchId?: number | null;
  userIds: string[];
}

export interface ClawbackPreview {
  operation: ClawbackOperation | string;
  companyId: number;
  matchId?: number | null;
  tournamentId?: number | null;
  /** Empty for a caller who is neither HR nor Admin. */
  officials: ClawbackOfficial[];
  totalsByCurrency: ClawbackCurrencyTotal[];
  /** Bulk import only: which file rows carry the claw-back. */
  rows?: ClawbackRow[] | null;
  /** HR only. Opaque, 15 minutes. */
  consentToken?: string | null;
  expiresAtUtc?: string | null;
  stale: boolean;
}

/** Optional consent passed to the API calls that can claw back pay. */
export interface ClawbackConsentOption {
  clawbackConsent?: string;
}

export interface BulkImportHeldRow {
  rowIndex: number;
  matchId?: number | null;
  reason: 'clawbackNotApproved' | 'clawbackStale' | 'clawbackRequiresHr' | string;
}

/** `GET Tournament/DeletePreview/{id}` (HrOnly). Counts only, measured on the server. */
export interface TournamentDeletionPreviewView {
  tournamentId: number;
  name: string;
  startDate: string;
  endDate: string;
  status: TournamentStatus | number | string;
  isArchived: boolean;
  matchCount: number;
  matchesByStatus: { status: MatchStatus | number | string; count: number }[];
  liveAssignmentCount: number;
  archivedAssignmentCount: number;
  ratingCount: number;
  historyRowCount: number;
  scheduleBlockCount: number;
  scheduledShiftsToDelete: number;
  attachmentCount: number;
  scheduledShiftsRetained: number;
  clockInsKept: number;
  legacyMatchShiftsKept: number;
  hourlyPayItemsKept: number;
  pendingSwapsToCancel: number;
  assignmentsNeverPaid: number;
  assignmentsOnUnconsumedPay: number;
  assignmentsOnConsumedPay: number;
  assignmentsOnArchivedPay: number;
  unconsumedReductionByCurrency: TournamentDeletionCurrencyAmount[];
  clawbackByCurrency: TournamentDeletionCurrencyAmount[];
  ratingChanges: TournamentDeletionRatingChange[];
  officialsToNotify: number;
  upcomingMatchesRemoved: number;
  recurring: TournamentDeletionRecurringInfo;
  /** `OPEN_SESSION` | `UNQUANTIFIABLE_PAY_LINK` */
  blockers: string[];
  openSessionCount: number;
  unquantifiablePayLinkCount: number;
  fingerprint: string;
  /** Round 2: who loses which amount, per currency and kind (same shape as the consent preview). */
  clawbackOfficials?: ClawbackOfficial[];
}

export interface DeleteTournamentInput {
  confirmName: string;
  fingerprint: string;
  seriesChoice?: TournamentSeriesChoice;
}

export interface TournamentDeletionResultView {
  deleted: boolean;
  /** Fresh preview on a 409 (stale preview or blockers). */
  preview?: TournamentDeletionPreviewView | null;
  matchesDeleted: number;
  assignmentsDeleted: number;
  scheduledShiftsDeleted: number;
  scheduledShiftsRetained: number;
  swapsCancelled: number;
  payLinksVoided: number;
  officialsNotified: number;
  snapshotSaved: boolean;
}

export interface CreateMatchInput {
  tournamentId: number;
  team1Name: string;
  team2Name: string;
  location: string;
  pitch?: string;
  ageCategory?: AgeCategory;
  division?: string;
  scheduledDateTime: string;
  scheduledTimeUnknown?: boolean;
  type: MatchType;
  format: MatchFormat;
  /** Optional per-match length in minutes (5..1200). Omitted = the company's duration for the format. */
  durationMinutesOverride?: number;
  requiredCertification?: RefereeCertification;
  refereeTeamMembers?: { userId: string; role: MatchRole; workPointOverride?: number }[];
}

export interface UpdateMatchInput {
  id: number;
  team1Name: string;
  team2Name: string;
  location: string;
  pitch?: string;
  ageCategory?: AgeCategory;
  division?: string;
  scheduledDateTime: string;
  scheduledTimeUnknown?: boolean;
  type: MatchType;
  format: MatchFormat;
  /** Optional per-match length in minutes (5..1200). Omitted KEEPS the match's current duration. */
  durationMinutesOverride?: number;
  /** true = drop a per-match override; the company duration for the format applies again. */
  resetDurationToDefault?: boolean;
  requiredCertification?: RefereeCertification;
  /**
   * CO-921 review: `workPointOverride: null` CLEARS a member's override; an OMITTED property KEEPS it
   * (older mobile builds send only {userId, role}). Always send the field, null when blank.
   */
  refereeTeamMembers?: { userId: string; role: MatchRole; workPointOverride: number | null }[];
}

export interface CompleteMatchInput {
  matchId: number;
  team1Score: number;
  team2Score: number;
  extraTimeTeam1Score?: number;
  extraTimeTeam2Score?: number;
  penaltyTeam1Score?: number;
  penaltyTeam2Score?: number;
  comments?: string;
  refereeRatings?: { refereeTeamMemberId: number; rating: number }[];
}

export interface UpdateMatchStatusInput {
  matchId: number;
  newStatus: MatchStatus;
  reason?: string;
}

export interface MatchListQuery {
  tournamentId?: number;
  search?: string;
  status?: MatchStatus;
  location?: string;
  pitch?: string;
  ageCategory?: AgeCategory;
  division?: string;
  completedOnly?: boolean;
  /** HR/Admin-only — filter to matches where a specific referee is assigned. */
  refereeUserId?: string;
  sortBy?: string;
  sortDescending?: boolean;
  page?: number;
  pageSize?: number;
}

export interface AssignRefereeInput {
  matchId: number;
  refereeProfileId: number;
  role: MatchRole;
  notifyParties: boolean;
  reason?: string;
  workPointOverride?: number;
  /** Replace the referee with this UserId on the match (their assignment is archived; requires `reason`). */
  replaceUserId?: string;
}

export interface CreateRefereeProfileInput {
  userId: string;
  certification: RefereeCertification;
  /** Per-referee rate override; null = use the company-wide rule rate. */
  pointToCurrencyRate?: number | null;
}

export interface UpdateRefereeProfileInput {
  id: number;
  certification: RefereeCertification;
  rating?: number;
  /** Per-referee rate override; null clears it (company-wide rule rate applies). */
  pointToCurrencyRate?: number | null;
}

export interface RefereeListQuery {
  search?: string;
  certification?: RefereeCertification;
  page?: number;
  pageSize?: number;
}

// ============================================================================
// Work Point Input Interfaces
// ============================================================================

export interface CreateWorkPointRuleInput {
  roleFormatPoints: WorkPointRoleFormatPoints[];
  weekendHolidayBonus: number;
  nightMatchBonus: number;
  nightMatchThresholdHour: number;
  earlyMorningBonus: number;
  earlyMorningThresholdHour: number;
  highRatingThreshold: number;
  highRatingBonus: number;
  midRatingThreshold: number;
  midRatingBonus: number;
  minPointsPerMatch: number;
  maxPointsPerMatch: number;
  pointToCurrencyRate: number;
  effectiveFrom: string;
  effectiveTo?: string;
}

export interface UpdateWorkPointRuleInput extends CreateWorkPointRuleInput {
  id: number;
}

export interface WorkPointEstimateQuery {
  type: MatchType;
  format: MatchFormat;
  role: MatchRole;
  refereeProfileId?: number;
  scheduledDateTime?: string;
}

export interface CompensationReportQuery {
  startDate: string;
  endDate: string;
  refereeUserId?: string;
  tournamentId?: number;
  /** Server-paged (default 25, clamped to 100). Prefer `getCompensationReportAllPages`. */
  page?: number;
  pageSize?: number;
}

export interface MyAssignmentsQuery {
  page?: number;
  pageSize?: number;
}

export interface MyWorkPointsQuery {
  startDate?: string;
  endDate?: string;
}

// ============================================================================
// Standings
// ============================================================================

export interface StandingEntry {
  teamName: string;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifference: number;
  points: number;
}

// ============================================================================
// Bracket
// ============================================================================

export interface BracketMatchView {
  matchId: number;
  team1Name: string;
  team2Name: string;
  status: MatchStatus;
  bracketPosition: number;
  team1Score?: number;
  team2Score?: number;
  scheduledDateTime?: string;
  winner?: string;
}

export interface BracketRound {
  roundNumber: number;
  roundName: string;
  matches: BracketMatchView[];
}

export interface BracketView {
  tournamentId: number;
  tournamentName: string;
  rounds: BracketRound[];
}

export interface GenerateBracketInput {
  teamNames: string[];
  defaultLocation: string;
  firstRoundDateTime: string;
  hoursBetweenMatches: number;
  daysBetweenRounds: number;
  format: MatchFormat;
  type: MatchType;
  allowRegenerate?: boolean;
  ageCategory?: AgeCategory;
  division?: string;
  pitch?: string;
}

// ============================================================================
// Live Score & Reschedule
// ============================================================================

export interface RescheduleMatchInput {
  matchId: number;
  newScheduledDateTime: string;
  reason?: string;
}

export interface UpdateLiveScoreInput {
  matchId: number;
  team1Score: number;
  team2Score: number;
}

// ============================================================================
// Bulk Import
// ============================================================================

export interface BulkImportMatchRow {
  day: string;
  venue: string;
  pitch?: string;
  time: string;
  ageCategory?: string;
  division?: string;
  /** Optional raw value from a FORMAT column ("5-aside", "7v7", …). Blank falls back to the dialog default. */
  format?: string;
  /** Optional raw value from a TYPE column ("Indoor" / "Outdoor"). Blank falls back to the dialog default. */
  type?: string;
  /** Optional raw value from a DURATION column in minutes ("25", "25 min"). Blank = the company's duration for the format. */
  duration?: string;
  team1Name: string;
  team2Name: string;
  /** Optional raw value from a STATUS column (e.g. "Cancelled"). */
  status?: string;
  commissioner?: string;
  headReferee?: string;
  assistantReferee1?: string;
  assistantReferee2?: string;
  fourthOfficial?: string;
}

export interface BulkImportMatchesInput {
  tournamentId: number;
  weekStartDate: string;
  defaultFormat: MatchFormat;
  defaultType: MatchType;
}

export interface BulkImportPreviewView {
  rows: BulkImportMatchRow[];
  totalRows: number;
  validRows: number;
  warnings: string[];
  availableVenues: string[];
  availableAgeCategories: string[];
  availableDivisions: string[];
}

/** Why a name in an official column produced no assignment. */
export enum UnresolvedOfficialReason {
  /** No company user carries this name (nor anything close to it). */
  UnknownName = 0,
  /** The person IS an employee of this company, but has no referee profile. */
  NotAReferee = 1,
  /** Two or more referees are equally close to the spelling — refused rather than guessed. */
  Ambiguous = 2,
  /** Resolved, but the same person was already taken by an earlier role column on the row. */
  DuplicateRole = 3,
  /** Resolved, but the match row is cancelled so no roster was touched. */
  MatchCancelled = 4,
  /** Resolved, but the person already holds a DIFFERENT role on this same match. */
  AlreadyOnMatch = 5,
  /** Resolved, but already assigned to another match whose time overlaps; candidates[0] names that match. */
  OverlappingBooking = 6,
  /** Resolved, but the person declared that DAY unavailable in My Schedule. `candidates` is empty. */
  SelfUnavailable = 7,
}

/** A name that resolved only after tolerating a spelling difference — confirm before importing. */
export interface ApproximateMatchView {
  rowIndex: number;
  /** The spelling as it appeared in the sheet. */
  name: string;
  /** The company's spelling of the person who will be assigned. */
  matchedName: string;
  roleLabel: string;
}

export interface UnresolvedOfficialView {
  rowIndex: number;
  name: string;
  roleLabel: string;
  reason: UnresolvedOfficialReason;
  /** For Ambiguous, the names it could not choose between; for OverlappingBooking, the clashing match. */
  candidates: string[];
}

export enum BulkImportRowAction {
  New = 0,
  Update = 1,
  Skip = 2,
  Error = 3,
  /** Round 2: not applied because the claw-back it carries was not approved by HR. */
  Held = 4,
}

export interface BulkImportRowResult {
  rowIndex: number;
  action: BulkImportRowAction;
  /** Blocking error message (rare). */
  message?: string;
  /** Non-blocking notes for a row that was still imported. */
  warnings: string[];
}

export interface MatchBulkImportResultView {
  created: number;
  updated: number;
  skipped: number;
  /** Rows imported with one or more warnings. */
  warningCount: number;
  errors: string[];
  rowResults: BulkImportRowResult[];
  /** Officials named in the file that did NOT end up on a match. */
  unresolvedOfficials: UnresolvedOfficialView[];
  /** Names assigned only after tolerating a spelling difference. */
  approximateMatches: ApproximateMatchView[];
  /** New matches whose FORMAT cell was blank, so they take the dialog default. */
  rowsUsingDefaultFormat: number;
  /** New matches whose TYPE cell was blank, so they take the dialog default. */
  rowsUsingDefaultType: number;
  /** Round 2: rows not applied because their claw-back was not approved. Null when none. */
  heldRows?: BulkImportHeldRow[] | null;
  /** Round 2: fresh preview + token for exactly the held rows. */
  clawback?: ClawbackPreview | null;
}

export interface BulkImportAnalyzeResultView {
  rowResults: BulkImportRowResult[];
  newCount: number;
  updateCount: number;
  skipCount: number;
  errorCount: number;
  /** Rows (New/Update) that carry one or more warnings. */
  warningCount: number;
  errors: string[];
  /** Officials named in the file that will NOT end up on a match. */
  unresolvedOfficials: UnresolvedOfficialView[];
  /** Names that will be assigned only after tolerating a spelling difference. */
  approximateMatches: ApproximateMatchView[];
  /** New matches whose FORMAT cell is blank, so they take the dialog default. */
  rowsUsingDefaultFormat: number;
  /** New matches whose TYPE cell is blank, so they take the dialog default. */
  rowsUsingDefaultType: number;
  /** Round 2: what the batch would take back from officials (token for the whole batch). Null when nothing. */
  clawback?: ClawbackPreview | null;
}

export interface BulkImportMatchesRequest {
  input: BulkImportMatchesInput;
  rows: BulkImportMatchRow[];
}

// ============================================================================
// Utility
// ============================================================================

/** Resolves a numeric or string enum value, returning the number. */
export function resolveEnum<T extends Record<string, number | string>>(
  enumObj: T,
  value: unknown
): number | undefined {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const found = enumObj[value as keyof T];
    if (typeof found === 'number') return found;
  }
  return undefined;
}
